import React from "react";
import DOMPurify from "dompurify";
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import { addHttp } from "../Quill-Text-Editor/utils";
import { useMemo } from "react";
import "./styles.css";

/**
 * Adds a hook to DOMPurify to set all elements owning target to target=_blank.
 * This enhances security when rendering user-generated content with links.
 * @param {object} node - The DOM element to sanitize.
 */
DOMPurify.addHook("afterSanitizeAttributes", function (node) {
  // Check if the node is an anchor element with an href attribute
  if (node.tagName === "A" && node.hasAttribute("href")) {
    const href = node.getAttribute("href");
    // Check if the href contains "abundowealth.com"
    if (!href.includes("abundowealth.com")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    } else {
      node.removeAttribute("target");
      node.removeAttribute("rel");
    }
  }
});



/**
 * RichTextDisplay - Converts a Quill Delta object to sanitized HTML and renders it.
 * Optionally highlights occurrences of a search query within the rendered output.
 *
 * @param {Object} props
 * @param {{ ops: Object[] }} props.content - Quill Delta content object.
 * @param {string} [props.thisClassName] - CSS class applied to the container div.
 * @param {string} [props.query] - Optional search term; matching text is wrapped in a `.highlighted` span.
 * @returns {JSX.Element}
 */
const RichTextDisplay = ({ content, thisClassName,query }) => {
  /** HTML string produced by converting `content.ops` via QuillDeltaToHtmlConverter. */
  const html = useMemo(() => {
    // Handle HTML string content (new TipTap format)
    if (typeof content === 'string') {
      return content;
    }
    // Handle Quill Delta content (legacy format)
    if (!content?.ops) return '';
    const converter = new QuillDeltaToHtmlConverter(content.ops, {
      linkRel: "noreferrer noopener",
      linkTarget: "_blank",
      urlSanitizer: addHttp,
      inlineStyles: {
        indent: (value, op) => {
          var indentSize = parseInt(value, 10) * 3;
          var side = op.attributes["direction"] === "rtl" ? "right" : "left";
          return "padding-" + side + ":" + indentSize + "em";
        },
        list: (value, op) => {
          var indentSize = parseInt(value, 10) * 3;
          var side = op.attributes["direction"] === "rtl" ? "right" : "left";
          return "padding-" + side + ":" + indentSize + "em";
        },
        direction: (value, op) => {
          if (value === "rtl") {
            return (
              "direction:rtl" +
              (op.attributes["align"] ? "" : "; text-align: inherit")
            );
          } else {
            return "";
          }
        },
      },
    });
    return converter.convert();
  }, [content]);


  /** DOMPurify-sanitized version of `html`, safe for `dangerouslySetInnerHTML`. */
  const sanitizedHtml = DOMPurify.sanitize(html);

  /** `dangerouslySetInnerHTML` value: either plain sanitized HTML or query-highlighted HTML. */
  const renderContent = useMemo(() => {
    if (query && query.trim() !== "") {
      // Escape special characters in the query to safely use it in a regex pattern
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedQuery, 'gi');
      const highlightedHtml = sanitizedHtml.replace(regex, match => `<span class="highlighted">${match}</span>`);
      return { __html: highlightedHtml };
    } else {
      return { __html: sanitizedHtml };
    }
  }, [sanitizedHtml, query]);

  
  return (
    <div
      className={thisClassName}
      dangerouslySetInnerHTML={renderContent}
    ></div>
  );
};

export default RichTextDisplay;
